import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Heart } from "lucide-react";
import type { Project } from "@mangelapp/shared";
import { cn } from "@/lib/utils";
import { useDateFormat } from '@/utils/date-formatter';
import { useProjectStatusDisplay } from '@/hooks/use-project-statuses';
import { isProjectDateBeforeDay } from '@/utils/project-dates';

// Projektbild-Komponente
function ProjectImage({ project }: { project: Project }) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let mounted = true;
    
    const loadImage = async () => {
      if (!project.bildUrl) {
        return;
      }

      // Legacy Bilder - zeige blauen Fallback da nicht verfügbar
      if (project.bildUrl.startsWith('/legacy/')) {
        if (mounted) {
          setHasError(true);
        }
        return;
      }

      // Base64-Bilder SOFORT anzeigen!
      if (project.bildUrl.startsWith('data:image/')) {
        if (mounted) {
          setImageUrl(project.bildUrl);
        }
        return;
      }

      // Externe URLs direkt laden
      if (!project.bildUrl.startsWith('/objects/uploads/')) {
        if (mounted) {
          setImageUrl(project.bildUrl);
        }
        return;
      }

      // Legacy Object Storage - als Fallback anzeigen
      if (mounted) {
        setHasError(true);
      }
    };

    loadImage();

    return () => {
      mounted = false;
    };
  }, [project.id, project.bildUrl]);

  if (!project.bildUrl || hasError) {
    return (
      <div className="w-full h-full bg-[#DDDDDD] flex items-center justify-center">
        <img
          src="/images/logos/logo_outline.png"
          alt=""
          className="w-16 h-16 object-contain opacity-80"
          aria-hidden="true"
        />
      </div>
    );
  }

  if (!imageUrl) {
    // Loading state
    return <div className="w-full h-full bg-gray-200" />;
  }

  return (
    <img 
      src={imageUrl} 
      alt={project.name}
      className="w-full h-full object-cover"
      onError={(e) => {
        console.warn('Projektbild konnte nicht angezeigt werden:', imageUrl);
        setHasError(true);
      }}
      onLoad={() => {
        // Image loaded successfully
      }}
    />
  );
}

interface ProjectCardProps {
  project: Project;
  ticketStats?: { total: number; totalLikes: number };
  onClick: () => void;
  onDelete?: (projectId: string) => void;
  squareImage?: boolean; // If true, image will be square and take more space
}

export default function ProjectCard({ project, ticketStats, onClick, onDelete, squareImage = false }: ProjectCardProps) {
  const { t, i18n } = useTranslation(['components.projectCard', 'components.ticket']);
  const locale = i18n.language?.startsWith('de') ? 'de-DE' : i18n.language?.startsWith('tr') ? 'tr-TR' : 'en-US';
  const { formatDate } = useDateFormat();
  
  // Use database-driven project status and priority
  const { displayName: statusDisplayName, color: statusColor, status: projectStatus } = useProjectStatusDisplay(
    (project as any).projectStatusId || null
  );
  
  // Determine project state by semanticType (language-agnostic)
  const semantic = (projectStatus?.semanticType || '').toLowerCase();
  const isCompleted = semantic === 'completed';
  const isPaused = semantic === 'paused';
  
  const isOverdue = !!project.endDatum && isProjectDateBeforeDay(project.endDatum, new Date()) && !isCompleted;





  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent navigation if clicking on dialog elements
    if ((e.target as HTMLElement).closest('[role="dialog"]')) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    onClick();
  };

  const imageClassName = squareImage
    ? "relative w-full flex-[0_0_50%] overflow-hidden"
    : "relative w-full flex-[0_0_35%] overflow-hidden";

  return (
    <Card className="card-container card-container-muted card-container-no-shadow overflow-hidden cursor-pointer flex flex-col max-w-[300px] w-full aspect-[660/1115]" onClick={handleCardClick}>
      {/* Project Image */}
      <div className={imageClassName}>
        <ProjectImage project={project} />

      </div>
      
      <CardContent className="p-4 relative flex flex-col min-h-0 flex-[0_0_50%] overflow-auto">
        <div className="mb-1">
          <div className="flex items-start min-h-[24px]">
            <h3 className="text-project-name truncate">
              {project.name}
            </h3>
          </div>
          {project.projektNummer && (
            <div className="text-project-number truncate">
              {t('components.projectCard:labels.projectNo', { num: project.projektNummer })}
            </div>
          )}
        </div>
        
        {/* Project Details */}
        <div className="space-y-1 mb-2">
          {/* Address */}
          <div className="text-project-detail truncate">
            {(() => {
              // Build address from structured fields
              const street = project.street;
              const houseNumber = project.houseNo;
              const city = project.city;
              
              if (street && houseNumber) {
                return `${street} ${houseNumber}${city ? `, ${city}` : ''}`;
              }
              if (street) {
                return `${street}${city ? `, ${city}` : ''}`;
              }
              // Last resort: show city if available
              if (city) {
                return city;
              }
              return '-';
            })()}
          </div>
          {/* Project Lead */}
          <div className="text-project-detail">
            {t('components.projectCard:labels.manager', { name: project.managerName || '-' })}
          </div>
          {/* Due Date */}
          <div
            className="text-project-detail"
            style={isOverdue ? { color: '#ff0000' } : undefined}
          >
            {t('components.projectCard:labels.dueBy', { date: project.endDatum ? formatDate(project.endDatum) : t('components.projectCard:labels.notSet') })}
            {isOverdue && ` ${t('components.projectCard:labels.overdue')}`}
          </div>
        </div>

        {/* Progress */}
        <div className="space-y-1.5 mb-2">
          <div className="flex items-center justify-between">
            <span className="text-project-progress">{t('components.projectCard:labels.progress')}</span>
            <span className="text-project-progress">{project.fortschritt}%</span>
          </div>
          <Progress 
            value={project.fortschritt || 0} 
            className={cn(
              "h-2 bg-slate-100 [&>div]:relative [&>div]:after:content-[''] [&>div]:after:absolute [&>div]:after:right-0 [&>div]:after:top-1/2 [&>div]:after:-translate-y-1/2 [&>div]:after:w-2 [&>div]:after:h-2 [&>div]:after:rounded-full [&>div]:after:border [&>div]:after:border-white [&>div]:after:shadow-sm",
              project.fortschritt === 100 
                ? "[&>div]:bg-gradient-to-r [&>div]:from-green-500 [&>div]:to-green-600 [&>div]:after:bg-green-700"
                : isPaused
                ? "[&>div]:bg-gradient-to-r [&>div]:from-yellow-500 [&>div]:to-yellow-600 [&>div]:after:bg-yellow-700"
                : "[&>div]:bg-gradient-to-r [&>div]:from-blue-500 [&>div]:to-blue-600 [&>div]:after:bg-blue-700"
            )}
          />
        </div>

        <div className="relative flex items-center justify-center mt-auto">
          <span
            className="px-4 py-1 rounded-full text-xs font-semibold min-w-[120px] text-center"
            style={{
              backgroundColor: statusColor || '#9ca3af',
              color: '#ffffff',
            }}
          >
            {statusDisplayName}
          </span>

          {/* Like-Summenzähler */}
          {ticketStats && (
            <div className="absolute right-0 flex flex-col items-center">
              <Heart className="h-5 w-5 text-slate-600" />
              <span className="text-project-like-count">
                {ticketStats.totalLikes}
              </span>
            </div>
          )}
        </div>

      </CardContent>

    </Card>
  );
}
